import { FileSources, ProtectedFilePayload } from "constants/sockets";
import * as React from "react";
import { audioPlayer } from "utils/audio";
import { useSocketContext } from "../../context/SocketContext";
import { Email } from "../emailTypes";

const audio = audioPlayer(
	"https://riddlestory-bucket1.s3.eu-central-1.amazonaws.com/assets/mixkit-long-pop-2358.wav",
	0.5,
	true,
);

export function useMails(roomId: string) {
	const { socket, connected } = useSocketContext();
	const [mails, setMails] = React.useState<Email[]>([]);

	const markMailAsRead = React.useCallback(
		(mailId: string) => {
			socket?.emit("markMailAsRead", { roomId, mailId });
		},
		[roomId, socket],
	);
	React.useEffect(() => {
		if (!socket) return;

		const onMailHistory = (mailHistory: Email[]) => {
			setMails(mailHistory);
		};

		const onIncomingMail = (mail: Email) => {
			audio.play();
			setMails((prev) => [mail, ...prev]);
			audio.setDelayedAutoPause();
		};

		const onMailMarkedAsRead = ({ id }) => {
			setMails((prev) =>
				prev.map((mail) => {
					if (mail.id === id) {
						return {
							...mail,
							isRead: true,
						};
					}
					return mail;
				}),
			);
		};

		const updateMailHistory = (data: ProtectedFilePayload) => {
			if (data.fileSource === FileSources.EMAIL_ATTACHMENT) {
				setMails((prevMails) => {
					return prevMails.map((mail) => (mail.id === data.payload.id ? data.payload : mail));
				});
			}
		};

		// Init listeners
		socket.on("mailHistory", onMailHistory);
		socket.on("incomingMail", onIncomingMail);
		socket.on("markMailAsRead", onMailMarkedAsRead);
		socket.on("updateFileAcccess", updateMailHistory);

		// Request mail history once
		socket.emit("getMailHistory", { roomId });

		return () => {
			socket.off("mailHistory", onMailHistory);
			socket.off("incomingMail", onIncomingMail);
			socket.off("updateFileAcccess", updateMailHistory);
		};
	}, [roomId, socket]);

	React.useEffect(() => {
		if (!socket) return;
		if (connected) {
			socket.emit("getMailHistory", { roomId });
		}
	}, [connected, roomId, socket]);

	return { mails, markMailAsRead };
}
